import { StripeOrderStatus } from "../../Stripe/StripeOrderDTO.ts";
import { SALES_DEFINITIONS } from "./salesDefinitions.ts";
import { HeyLightOrderStatus } from "../../HeyLight/heyLightOrderDTO.ts";
import { BonificoOrderStatus } from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { SaleRecord, SalesGateway } from "./SaleRecord.ts";
import { ItemRefunds } from "./salesBreakdowns.ts";
import { SalesPeriod } from "./salesPeriod.ts";
import { OrdersLinksMenu } from "./OrdersLinksMenu.tsx";
import { ordersListPathFor } from "./ordersListLinks.ts";
import {
  centsForCsv,
  downloadSalesCsv,
  ratioForCsv,
  toCsv,
} from "./salesCsv.ts";
import { formatCount, formatEuros, formatPercent } from "./salesFormat.ts";
import { EmptyStatistics, LoadingBlock } from "./StatisticsCard.tsx";
import { StatisticsCardWithDetails } from "./StatisticsCardWithDetails.tsx";
import { refundedToCustomerInCents } from "./salesCustomers.ts";
import {
  CUSTOMER_IDENTITY_COLUMNS,
  CUSTOMER_ORDER_COUNT_COLUMN,
  CUSTOMER_REFUNDED_COLUMN,
  CUSTOMER_SOLD_COLUMN,
  ORDER_AMOUNT_COLUMN,
  ORDER_CUSTOMER_COLUMNS,
  ORDER_DATE_COLUMN,
  ORDER_GATEWAY_COLUMN,
  ORDER_ID_COLUMN,
  ORDER_REFUND_DATE_COLUMN,
  ORDER_REFUNDED_COLUMN,
  orderItemColumn,
} from "./salesDetailsColumns.tsx";

const REFUNDED_STATUSES: Record<SalesGateway, string[]> = {
  [SalesGateway.Stripe]: [
    StripeOrderStatus.Refunded,
    StripeOrderStatus.PartiallyRefunded,
  ],
  [SalesGateway.HeyLight]: [
    HeyLightOrderStatus.Refunded,
    HeyLightOrderStatus.PartiallyRefunded,
  ],
  [SalesGateway.Bonifico]: [
    BonificoOrderStatus.Refunded,
    BonificoOrderStatus.PartiallyRefunded,
  ],
};

export function RefundsByItemCard({
  refunds,
  refundedSales,
  itemLabelOf,
  period,
  isLoading,
}: {
  refunds: ItemRefunds[];
  refundedSales: SaleRecord[];
  itemLabelOf: (itemKey: string) => string;
  period: SalesPeriod;
  isLoading: boolean;
}) {
  const exportCsv = () =>
    downloadSalesCsv({
      section: "rimborsi",
      period,
      csv: toCsv(refunds, [
        {
          header: "Prodotto o bundle",
          valueOf: (item) => itemLabelOf(item.itemKey),
        },
        { header: "ID", valueOf: (item) => item.itemKey },
        {
          header: "Venduto (€)",
          valueOf: (item) => centsForCsv(item.soldInCents),
        },
        {
          header: "Rimborsato (€)",
          valueOf: (item) => centsForCsv(item.refundedInCents),
        },
        {
          header: "Ordini rimborsati",
          valueOf: (item) => item.refundedOrderCount,
        },
        {
          header: "% rimborsato",
          valueOf: (item) => ratioForCsv(item.refundRate),
        },
      ]),
    });

  return (
    <StatisticsCardWithDetails
      title="Rimborsi per prodotto"
      info={SALES_DEFINITIONS.refundsByItem}
      description="Rimborsi, anche successivi, sugli ordini conclusi nel periodo"
      csvSection="rimborsi"
      period={period}
      isLoading={isLoading}
      exportSummaryCsv={refunds.length > 0 ? exportCsv : undefined}
      orders={refundedSales}
      orderColumns={[
        ORDER_ID_COLUMN,
        ORDER_DATE_COLUMN,
        ...ORDER_CUSTOMER_COLUMNS,
        orderItemColumn(itemLabelOf),
        ORDER_GATEWAY_COLUMN,
        ORDER_AMOUNT_COLUMN,
        ORDER_REFUNDED_COLUMN,
        ORDER_REFUND_DATE_COLUMN,
      ]}
      customerColumns={[
        ...CUSTOMER_IDENTITY_COLUMNS,
        CUSTOMER_ORDER_COUNT_COLUMN,
        CUSTOMER_SOLD_COLUMN,
        CUSTOMER_REFUNDED_COLUMN,
      ]}
      rankCustomersBy={refundedToCustomerInCents}
      noOrdersMessage="Nessun rimborso sugli ordini del periodo"
      summary={
        isLoading ? (
          <LoadingBlock className="h-48 w-full" />
        ) : refunds.length === 0 ? (
          <EmptyStatistics>
            Nessun rimborso sugli ordini del periodo
          </EmptyStatistics>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-3 font-medium">Prodotto o bundle</th>
                  <th className="py-2 pr-3 font-medium text-right">Venduto</th>
                  <th className="py-2 pr-3 font-medium text-right">
                    Rimborsato
                  </th>
                  <th className="py-2 pr-3 font-medium text-right">
                    Ordini rimborsati
                  </th>
                  <th className="py-2 font-medium text-right">% rimborsato</th>
                </tr>
              </thead>
              <tbody>
                {refunds.map((item) => (
                  <tr
                    key={item.itemKey}
                    className="border-b border-gray-100 text-gray-700"
                  >
                    <td className="py-2 pr-3 min-w-40">
                      <div className="flex items-start gap-1">
                        <span className="break-words">
                          {itemLabelOf(item.itemKey)}
                        </span>
                        <OrdersLinksMenu
                          label="Vedi gli ordini rimborsati del periodo"
                          links={Object.entries(
                            item.refundedOrderCountByGateway,
                          ).map(([gateway, orderCount]) => ({
                            gateway: gateway as SalesGateway,
                            orderCount: orderCount ?? 0,
                            path: ordersListPathFor({
                              gateway: gateway as SalesGateway,
                              itemKey: item.itemKey,
                              period,
                              statuses:
                                REFUNDED_STATUSES[gateway as SalesGateway],
                            }),
                          }))}
                        />
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      {formatEuros(item.soldInCents)}
                    </td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      {formatEuros(item.refundedInCents)}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {formatCount(item.refundedOrderCount)}
                    </td>
                    <td className="py-2 text-right font-medium">
                      {formatPercent(item.refundRate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    />
  );
}
